import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Get session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed - no session ID');
          navigate('/login');
          return;
        }

        // Exchange session_id for user data
        const response = await axios.post(`${API}/auth/google/callback`, {
          session_id: sessionId
        });

        if (response.data.success) {
          const { user, session_token } = response.data;
          
          // Store auth data
          sessionStorage.setItem('auth_token', session_token);
          sessionStorage.setItem('user', JSON.stringify(user));
          
          // Update auth context
          if (setUser) setUser(user);
          if (setToken) setToken(session_token);
          
          toast.success(`Welcome, ${user.full_name}!`);
          
          // Navigate to dashboard
          navigate('/', { state: { user } });
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processCallback();
  }, [navigate, setUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-primary">Completing sign in...</h2>
        <p className="text-sm text-slate-500 mt-1">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
